/**
 * Normalize track duration to whole seconds for queue/transport/UI.
 * Playlist rows may use duration, duration_sec, duration_seconds, or duration_ms.
 * Missing, zero, and non-finite values → undefined (never 0 in queue items).
 */

import { DURATION_SENTINEL_SECS, isAdvanceGuardDurationTrustworthy } from './trackDurationTrust';

export { DURATION_SENTINEL_SECS, isAdvanceGuardDurationTrustworthy, isDurationSentinel } from './trackDurationTrust';

/** Values above ~10h in "seconds" are treated as milliseconds. */
export const DURATION_MS_THRESHOLD_SECS = 36_000;

export function normalizeTrackDurationSecs(
  input: unknown,
  fallbackMsOrSecs?: unknown,
): number | undefined {
  const candidates = [input, fallbackMsOrSecs];
  for (const raw of candidates) {
    const secs = coerceDurationSecs(raw);
    if (secs != null) return secs;
  }
  return undefined;
}

function coerceDurationSecs(raw: unknown): number | undefined {
  if (raw == null || raw === '') return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0 || n >= DURATION_SENTINEL_SECS) return undefined;
  if (n > DURATION_MS_THRESHOLD_SECS) {
    const asSecs = Math.floor(n / 1000);
    return isAdvanceGuardDurationTrustworthy(asSecs) ? asSecs : undefined;
  }
  return Math.floor(n);
}

/** Read duration from a playlist/queue track row (any common field name). */
export function durationFromTrackRow(
  track: Record<string, unknown> | null | undefined,
): number | undefined {
  if (!track) return undefined;
  return normalizeTrackDurationSecs(
    track.duration ?? track.duration_sec ?? track.duration_seconds,
    track.duration_ms,
  );
}

/**
 * Normalize a track object at queue population time — seconds in `duration`,
 * omit duration when unknowable (never write 0).
 */
export function prepareTrackForQueue(
  track: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null | undefined {
  if (!track) return track;
  const secs = durationFromTrackRow(track);
  const {
    duration_ms: _durationMs,
    duration_sec: _durationSec,
    duration_seconds: _durationSeconds,
    duration: _duration,
    ...rest
  } = track;
  if (secs != null) {
    return { ...rest, duration: secs };
  }
  return { ...rest };
}
